import React, { useMemo } from "react";
import { getSizedImage } from "@/helpers/utils";
import { ProductCardList } from "@/features/product/ProductCardList";
import { productSelect, variantSelect } from "@/store";
import { PageModule } from "@/components/PageModule";
import { useMediaQuery } from "@mui/material";
import { theme } from "@/theme";
import { storefrontApi } from "@/services/storefront";
import { ProductCardProduct } from "@/features/product/ProductCard";
import { ShoppableProductCardProduct } from "@/features/product/ShoppableProductCard";
import { ProductCardListCollection } from "@/features/product/ProductCardListCollection";
import { groupBy } from "@/helpers/utils";

export interface ProductSliderProps {
  collectionHandle: string;
  numProducts?: number;
  numProductsDesktop?: number | undefined;
  condensedView?: boolean;
  shoppable?: boolean;
  layout?: "grid" | "carousel";
  className?: string;
  //This is to detect the first slider of a page. Based on it removed lazyloading for the first image. Improtant for LCP optimization
  firstSlider?: boolean;
  showProductCardListCollection?: boolean;
  collectionCount?: number;
  productHandles?: string[];
}
export const ProductSlider = ({
  collectionHandle,
  shoppable = true,
  numProducts = 50,
  numProductsDesktop,
  layout = "carousel",
  condensedView = false,
  className,
  firstSlider = false,
  showProductCardListCollection = false,
  collectionCount,
}: ProductSliderProps) => {
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  // Use numProducts for mobile, numProductsDesktop for desktop
  const effectiveNumProducts = isSmallScreen
    ? numProducts
    : numProductsDesktop || numProducts;

  const skeletonCards = useMemo<ProductCardProduct[]>(() => {
    return Array(effectiveNumProducts).fill({
      imageHeight: 440,
      imageWidth: 549,
      featuredImage: true,
      featuredText: !condensedView,
      type: true,
      ratings: true,
      url: !condensedView,
      title: true,
      badge: true,
      price: true,
      size: true,
    });
  }, [effectiveNumProducts, condensedView]);

  const collection = storefrontApi.useCollection(collectionHandle, {
    limit: collectionCount || effectiveNumProducts,
  });

  const productCards = useMemo<
    ShoppableProductCardProduct[] | undefined
  >(() => {
    if (collection.data === undefined) {
      return undefined;
    }

    console.log("collection.data", collection.data);

    const products = collection.data.all.filter((x) => !x.hidden);
    const grouped = groupBy(products, (p) => p.productGroup || p.id.toString());
    const selectedProducts = Array.from(grouped.values())
      .map((group) => group[0])
      .slice(0, effectiveNumProducts);

    return selectedProducts.map((product, i) => {
      const variant = productSelect.defaultVariant(product);
      const featuredText =
        product.featuredQuote && !condensedView
          ? `"${product.featuredQuote}"`
          : undefined;

      // Get the group for this product
      const groupKey = product.productGroup || product.id.toString();
      const groupProducts = grouped.get(groupKey) || [];

      return {
        id: variant.id,
        featuredImage: {
          src: getSizedImage(productSelect.featuredImage(product).url, "x400"),
          height: 440,
          width: 549,
          loading: firstSlider && i === 0 ? "eager" : "lazy",
        },
        badge: product.badge,
        featuredText,
        type: product.type,
        url: productSelect.url(product),
        title: product.title,
        size: variantSelect.sizeFormat(variant),
        compareAtPrice: variant.compareAtPrice,
        price: shoppable
          ? variant.price
          : variantSelect.lowestPrice(variant, product),
        product,
        variant,
        collectionProducts: groupProducts,
      };
    });
  }, [
    collection.data,
    condensedView,
    shoppable,
    firstSlider,
    effectiveNumProducts,
  ]);

  const data =
    productCards && shoppable
      ? ({
          shoppable: true,
          products: productCards,
        } as const)
      : ({
          shoppable: false,
          products: productCards ?? skeletonCards,
        } as const);

  return (
    <PageModule
      className={`${
        isSmallScreen && layout === "carousel" ? "px-0" : ""
      } ${className}`}
    >
      {showProductCardListCollection ? (
        layout === "carousel" ? (
          <ProductCardList layout={layout} {...data} />
        ) : (
          <ProductCardListCollection layout={layout} {...data} />
        )
      ) : (
        <ProductCardList layout={layout} {...data} />
      )}
    </PageModule>
  );
};
